import React from 'react';

import { Box } from '@atlaskit/primitives/compiled';
import { type AvatarProps } from '@atlaskit/avatar-group';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { ContentMode, Participant } from '../presenceTypes';
import { getParticipantColors, getCurrentUserColors, MAX_GROUP_SIZE } from '../presenceUtils';
import { useScrollToTelepointer } from '../useScrollToTelepointer';

import { PresenceAvatar } from './PresenceAvatar';

type PresenceAvatarProps = {
	index: number;
	participant: Participant;
	contentId: string;
	contentMode: ContentMode;
	contentType: string;
	isSingleParticipant: boolean;
	toggleEmptyPresenceDialog: () => void;
	avatarProps: AvatarProps;
	analyticSource?: string;
};

export const PresenceAvatarButton = ({
	index,
	participant,
	contentMode,
	contentType,
	contentId,
	isSingleParticipant,
	toggleEmptyPresenceDialog,
	avatarProps,
	analyticSource,
}: PresenceAvatarProps) => {
	const { id, color } = participant;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isScrollToFeatureEnabled, isEligibleToScroll, scrollToTelepointer } =
		useScrollToTelepointer({
			contentType,
			contentMode,
			// TODO: https://product-fabric.atlassian.net/browse/MDS-1320
			participant: participant as any,
		});

	const getAvatarButtonOnClickHandler = () => {
		if (isSingleParticipant) {
			return toggleEmptyPresenceDialog;
		}

		if (isEligibleToScroll) {
			return () => {
				const isScrollSuccessful = scrollToTelepointer();

				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						source: analyticSource,
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'presenceAvatarButton',
						product: 'confluence',
						attributes: {
							contentId,
							contentType,
							contentMode,
							isScrollToFeatureEnabled,
							isEligibleToScroll,
							isScrollSuccessful,
						},
					},
				}).fire();
			};
		}

		return participant?.onClick;
	};

	return (
		<Box padding="space.025" key={`box-${id}`} data-vc="presence-avatar">
			<PresenceAvatar
				{...avatarProps}
				src={avatarProps.src}
				name={avatarProps.name}
				activity={participant.activity}
				contentId={contentId}
				contentMode={contentMode}
				contentType={contentType}
				key={`avatar-${id}`}
				badgeTextColor={
					index === 0 ? getCurrentUserColors().textColor : getParticipantColors(id).textColor
				}
				borderColor={
					color ?? index === 0
						? getCurrentUserColors().backgroundColor
						: getParticipantColors(id).backgroundColor
				}
				stackIndex={MAX_GROUP_SIZE - index}
				testId={`team-presence-avatar-${index}`}
				onClick={getAvatarButtonOnClickHandler()}
				isEligibleToScroll={isEligibleToScroll}
				isScrollToFeatureEnabled={isScrollToFeatureEnabled}
			/>
		</Box>
	);
};
